/**
 * sanitize.utils.ts — Input sanitization utilities.
 *
 * Used at the service layer (before any Firestore write) and as Angular
 * validator factories. Angular templates auto-escape interpolated values,
 * so the primary attack surface here is:
 *   1. Length bombs   -> cost / storage abuse
 *   2. HTML injection -> strip tags so stored data is always plain text
 *   3. Control chars  -> zero-width, RTL override, null bytes
 *   4. Homograph / invisible chars -> strip dangerous unicode
 */

import { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';

// ── Limits ────────────────────────────────────────────────────────────────────
export const LIMITS = {
  DISPLAY_NAME: 50,
  ROOM_NAME:    60,
  EMAIL:        254,  // RFC 5321 max
  PASSWORD:     128,
  SEARCH_TERM:  50,
  MESSAGE:      500,
} as const;

// ── Dangerous unicode — explicit \\u escapes only, no embedded chars ──────────
//
// U+200B-U+200F: zero-width space, ZWNJ, ZWJ, LRM, RLM
// U+202A-U+202E: LRE, RLE, PDF, LRO, RLO (bidi direction overrides)
// U+2060-U+2064: word joiner, invisible math separators
// U+FEFF:        BOM / zero-width no-break space
// U+00AD:        soft hyphen
const DANGEROUS_UNICODE_RE   = new RegExp('[​-‏‪-‮⁠-⁤﻿­]', 'g');
const DANGEROUS_UNICODE_TEST = new RegExp('[​-‏‪-‮⁠-⁤﻿­]');

// ── Core strip functions ──────────────────────────────────────────────────────

function stripTags(value: string): string {
  return value.replace(/<[^>]*>/g, '');
}

// eslint-disable-next-line no-control-regex
const CONTROL_CHARS_RE = /[\x00-\x08\x0B\x0C\x0E-\x1F\x7F]/g;

function stripControlChars(value: string): string {
  return value.replace(CONTROL_CHARS_RE, '');
}

function stripDangerousUnicode(value: string): string {
  return value.replace(DANGEROUS_UNICODE_RE, '');
}

function normalizeWhitespace(value: string): string {
  return value.replace(/\s+/g, ' ').trim();
}

function sanitize(value: string, maxLen: number): string {
  if (typeof value !== 'string') return '';
  return normalizeWhitespace(
    stripDangerousUnicode(
      stripControlChars(
        stripTags(value)
      )
    )
  ).slice(0, maxLen);
}

// ── Public sanitizers ─────────────────────────────────────────────────────────

export function sanitizeDisplayName(name: string): string {
  return sanitize(name, LIMITS.DISPLAY_NAME);
}

export function sanitizeRoomName(name: string): string {
  return sanitize(name, LIMITS.ROOM_NAME);
}

export function sanitizeEmail(email: string): string {
  if (typeof email !== 'string') return '';
  return email.trim().toLowerCase().slice(0, LIMITS.EMAIL);
}

export function sanitizeSearchTerm(term: string): string {
  return sanitize(term, LIMITS.SEARCH_TERM);
}

export function sanitizeMessage(msg: string): string {
  return sanitize(msg, LIMITS.MESSAGE);
}

// ── Angular validator factories ───────────────────────────────────────────────

/** Rejects strings that still contain HTML tags. */
export function noHtmlValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const value: string = control.value ?? '';
    return /<[^>]+>/.test(value) ? { containsHtml: true } : null;
  };
}

/** Rejects strings containing Unicode direction-override or invisible chars. */
export function noUnicodeAbuse(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const value: string = control.value ?? '';
    return DANGEROUS_UNICODE_TEST.test(value) ? { unicodeAbuse: true } : null;
  };
}

/** Trims and rejects strings longer than max characters. */
export function maxTrimmedLength(max: number): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const value: string = (control.value ?? '').trim();
    return value.length > max
      ? { maxTrimmedLength: { max, actual: value.length } }
      : null;
  };
}
